// Raggruppamento per giorno di calendario per le cronologie (lead/offerta), stile chip WhatsApp.
// items sono già ordinati (desc, più recente prima); il gruppo di ogni item viene stabilito in
// base al giorno di calendario locale della sua data, mai su una finestra di 24h.

export interface DayGroup<T> {
  key: string;
  label: string;
  items: T[];
}

interface Ymd {
  y: number;
  m: number;
  d: number;
}

function toYmd(date: Date): Ymd {
  return { y: date.getFullYear(), m: date.getMonth(), d: date.getDate() };
}

function sameYmd(a: Ymd, b: Ymd): boolean {
  return a.y === b.y && a.m === b.m && a.d === b.d;
}

function parseValidDate(value: string | Date | null | undefined): Date | null {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function formatLabel(date: Date, now: Date): string {
  const dateYmd = toYmd(date);
  const nowYmd = toYmd(now);

  if (sameYmd(dateYmd, nowYmd)) return 'Oggi';

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (sameYmd(dateYmd, toYmd(yesterday))) return 'Ieri';

  const dayMonth = date.toLocaleDateString('it-IT', { day: 'numeric', month: 'long' });
  return dateYmd.y === nowYmd.y ? dayMonth : `${dayMonth} ${dateYmd.y}`;
}

export function groupItemsByDay<T>(
  items: T[],
  getDate: (item: T) => string | Date | null | undefined,
  now: Date = new Date(),
): DayGroup<T>[] {
  const groups: DayGroup<T>[] = [];
  let currentKey: string | null = null;

  for (const item of items) {
    const parsed = parseValidDate(getDate(item));

    if (!parsed) {
      // Data nulla/invalida: entra nel gruppo dell'item precedente, mai persa. Se è il primo
      // item della lista non esiste ancora un gruppo: se ne crea uno senza label.
      if (groups.length === 0) {
        groups.push({ key: 'no-date', label: '', items: [item] });
        currentKey = 'no-date';
      } else {
        groups[groups.length - 1].items.push(item);
      }
      continue;
    }

    const ymd = toYmd(parsed);
    const key = `${ymd.y}-${ymd.m}-${ymd.d}`;

    if (key === currentKey) {
      groups[groups.length - 1].items.push(item);
    } else {
      groups.push({ key, label: formatLabel(parsed, now), items: [item] });
      currentKey = key;
    }
  }

  return groups;
}
